import { MessageCircle, Send, AlertCircle } from "lucide-react";
import { useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FeedbackSection() {
  const [feedback, setFeedback] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!feedback.trim() || sending || submitted) return;
    setSending(true);
    setError(null);
    try {
      await axios.post(`${API}/feedback`, { message: feedback.trim() });
      setSubmitted(true);
      setTimeout(() => {
        setFeedback("");
        setSubmitted(false);
      }, 3000);
    } catch {
      setError("No se pudo enviar tu sugerencia. Inténtalo de nuevo.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 text-white mb-6">
            <MessageCircle className="w-8 h-8" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Ayúdanos a Mejorar ViralTubeAI
          </h2>
          <p className="text-lg text-gray-600">
            Tus sugerencias nos ayudan a crear una mejor herramienta para todos los creadores
          </p>
        </div>

        {/* Feedback Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8">
          <div className="mb-6">
            <label htmlFor="feedback" className="block text-sm font-semibold text-gray-700 mb-2">
              Comparte tus ideas, sugerencias o reporta problemas
            </label>
            <textarea
              id="feedback"
              data-testid="feedback-textarea"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Ejemplo: Me gustaría que la herramienta también analizara los comentarios del video..."
              className="w-full h-32 p-4 border-2 border-purple-200 rounded-xl focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none resize-none text-gray-700 placeholder-gray-400 transition-all"
              disabled={submitted}
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            data-testid="feedback-submit-button"
            disabled={!feedback.trim() || submitted || sending}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
          >
            {submitted ? (
              <>
                <span>¡Gracias por tu feedback!</span>
              </>
            ) : sending ? (
              <>
                <span>Enviando...</span>
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                <span>Enviar Sugerencia</span>
              </>
            )}
          </button>

          {error && (
            <div data-testid="feedback-error" className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Info text */}
          <p className="mt-4 text-sm text-gray-500 text-center">
            Leemos cada mensaje y valoramos tu opinión. Gracias por ayudarnos a mejorar.
          </p>
        </form>

        {/* Quick suggestions */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-600 mb-3">Sugerencias rápidas:</p>
          <div className="flex flex-wrap justify-center gap-2">
            {["Análisis de comentarios", "Comparar con competidores", "Exportar a PDF", "Análisis de canal completo"].map((suggestion, index) => (
              <button
                key={index}
                type="button"
                data-testid={`quick-suggestion-${index}`}
                className="px-4 py-2 bg-white border-2 border-purple-200 text-purple-700 rounded-full text-sm font-medium hover:bg-purple-50 hover:border-purple-300 transition-all"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
